import * as React from 'react';
import { connect } from 'react-redux';
import { IPackageListingAppState } from '../../redux-state';
import { updatePwycAmount } from '../../redux-state/actions/package-listing.actions';
import { getPwycMinAmount, getPwycAmount, isPwycPriceType } from '../../helpers/cart-helpers';
import { IPrice } from '../../custom/classes/production';

interface IExternalProps {
    performanceId: number;
    zoneId: number;
    prices: IPrice[];
}

interface IStateProps {
    pwycAmountTextboxLabel: string;
    pwycMinimumPriceMessaging: string;
    pwycMinAmount: number;
    priceTypeQuantity: Map<number, number>;
    pwycAmounts: Map<string, number>;
    pwycPriceTypeIds: number[];
    pwycZoneIds: number[];
}

interface IDispatchProps {
    updatePwycAmount: (performanceId: number, priceTypeId: number, amount: number) => void;
}

type PwycAmountInputProps = IExternalProps & IStateProps & IDispatchProps;

interface PwycAmountInputState {
    localAmounts: { [key: string]: number | string };
}

class PwycAmountInput extends React.Component<PwycAmountInputProps, PwycAmountInputState> {
    constructor(props: PwycAmountInputProps) {
        super(props);
        this.state = {
            localAmounts: {}
        };
    }

    componentDidMount() {
        this.ensureDefaultAmounts();
    }

    componentDidUpdate(prevProps: PwycAmountInputProps) {
        if (prevProps.priceTypeQuantity !== this.props.priceTypeQuantity ||
            prevProps.zoneId !== this.props.zoneId) {
            this.ensureDefaultAmounts();
        }
    }

    private ensureDefaultAmounts() {
        const eligiblePrices = this.getEligiblePrices();
        const seen = new Set<number>();
        eligiblePrices.forEach(price => {
            if (seen.has(price.priceTypeId)) return;
            seen.add(price.priceTypeId);
            const key = this.getAmountKey(price.priceTypeId);
            const storeAmount = this.props.pwycAmounts.get(key);
            if (storeAmount == null) {
                const minAmount = this.getMinAmountForPriceType(price);
                this.props.updatePwycAmount(this.props.performanceId, price.priceTypeId, minAmount);
            }
        });
    }

    private getEligiblePrices(): IPrice[] {
        return this.props.prices.filter(p => {
            const hasQuantity = this.props.priceTypeQuantity.has(p.priceTypeId)
                && this.props.priceTypeQuantity.get(p.priceTypeId) > 0;
            const isPwycEligible = this.props.pwycPriceTypeIds.length === 0
                || this.props.pwycPriceTypeIds.indexOf(p.priceTypeId) >= 0;
            const isZoneEligible = this.props.pwycZoneIds.length === 0
                || this.props.pwycZoneIds.indexOf(this.props.zoneId) >= 0;
            return hasQuantity && isPwycEligible && isZoneEligible;
        });
    }

    private getMinAmountForPriceType(price: IPrice): number {
        return Math.max(price.minPrice || 0, this.props.pwycMinAmount);
    }

    private getAmountKey(priceTypeId: number): string {
        return `${this.props.performanceId}_${priceTypeId}`;
    }

    private getCurrentAmount(priceTypeId: number, price: IPrice): number | string {
        const key = this.getAmountKey(priceTypeId);
        if (this.state.localAmounts[key] !== undefined) {
            return this.state.localAmounts[key];
        }
        const storeAmount = this.props.pwycAmounts.get(key);
        return storeAmount != null ? storeAmount : this.getMinAmountForPriceType(price);
    }

    private handleAmountChange(priceTypeId: number, price: IPrice, evt: React.ChangeEvent<HTMLInputElement>) {
        const value = evt.target.value;
        const key = this.getAmountKey(priceTypeId);

        if (value === "") {
            this.setState({ localAmounts: { ...this.state.localAmounts, [key]: "" } });
            return;
        }

        const newAmount = Number(value);
        if (isNaN(newAmount)) return;

        this.setState({ localAmounts: { ...this.state.localAmounts, [key]: value } });

        const minAmount = this.getMinAmountForPriceType(price);
        if (newAmount >= minAmount) {
            this.props.updatePwycAmount(this.props.performanceId, priceTypeId, newAmount);
        }
    }

    private handleAmountBlur(priceTypeId: number, price: IPrice) {
        const key = this.getAmountKey(priceTypeId);
        const minAmount = this.getMinAmountForPriceType(price);
        const currentValue = this.state.localAmounts[key];

        let finalAmount: number;
        if (currentValue === "" || currentValue === undefined || Number(currentValue) < minAmount) {
            finalAmount = minAmount;
        } else {
            finalAmount = Number(currentValue);
        }

        this.setState({ localAmounts: { ...this.state.localAmounts, [key]: finalAmount.toFixed(2) } });
        this.props.updatePwycAmount(this.props.performanceId, priceTypeId, finalAmount);
    }

    private currencyFormatter(amount: number): string {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    }

    render() {
        const eligiblePrices = this.getEligiblePrices();
        if (eligiblePrices.length === 0) return null;

        // Deduplicate by priceTypeId
        const seen = new Set<number>();
        const uniquePrices = eligiblePrices.filter(p => {
            if (seen.has(p.priceTypeId)) return false;
            seen.add(p.priceTypeId);
            return true;
        });

        const minAmounts = uniquePrices.map(p => this.getMinAmountForPriceType(p));
        const overallMin = Math.min(...minAmounts);

        return (
            <div className="stack-sm space-top-sm">
                {this.props.pwycMinimumPriceMessaging && overallMin > 0 &&
                    <p className="small" dangerouslySetInnerHTML={{
                        __html: this.props.pwycMinimumPriceMessaging.replace("{0}", this.currencyFormatter(overallMin))
                    }} />
                }
                {uniquePrices.map(price => {
                    const currentAmount = this.getCurrentAmount(price.priceTypeId, price);
                    const minAmount = this.getMinAmountForPriceType(price);
                    return (
                        <div key={`pwyc_${this.props.performanceId}_${price.priceTypeId}`}>
                            <label htmlFor={`pwyc_${this.props.performanceId}_${price.priceTypeId}`}>
                                {this.props.pwycAmountTextboxLabel}
                            </label>
                            <div className="currency-input">
                                <input
                                    id={`pwyc_${this.props.performanceId}_${price.priceTypeId}`}
                                    type="text"
                                    inputMode="decimal"
                                    value={typeof currentAmount === 'number' ? currentAmount.toFixed(2) : currentAmount}
                                    onChange={(e) => this.handleAmountChange(price.priceTypeId, price, e)}
                                    onBlur={() => this.handleAmountBlur(price.priceTypeId, price)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
}

function mapStateToProps(state: IPackageListingAppState, ownProps: IExternalProps): IStateProps {
    const perfSettings = state.packageListing.pwycPerformanceSettings?.[ownProps.performanceId];
    return {
        pwycAmountTextboxLabel: state.packageListingText.pwycAmountTextboxLabel,
        pwycMinimumPriceMessaging: state.packageListingText.pwycMinimumPriceMessaging,
        pwycMinAmount: perfSettings?.pwycMinAmount ?? 0,
        priceTypeQuantity: state.packageListing.priceTypeQuantity,
        pwycAmounts: state.packageListing.pwycAmounts,
        pwycPriceTypeIds: perfSettings?.pwycPriceTypeIds ?? [],
        pwycZoneIds: perfSettings?.pwycZoneIds ?? []
    };
}

function mapDispatchToProps(dispatch): IDispatchProps {
    return {
        updatePwycAmount: (performanceId, priceTypeId, amount) => {
            dispatch(updatePwycAmount(performanceId, priceTypeId, amount));
        }
    };
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(PwycAmountInput);
